(() => {
  'use strict';

  const mainSelector = '#MainContent';
  const sectionSelector = '.shopify-section:not(.header-section)';
  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );
  let cleanup;

  const itemSelector = [
    '.eyebrow',
    '[class*="eyebrow"]',
    '[class*="kicker"]',
    '[class*="overline"]',
    '[class*="heading"]',
    '[class*="title"]',
    '.ufm-heading',
    '.ufm-title',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    '.button',
    '.button-secondary',
    '.button-primary',
    '.link',
    'a.button',
    'button',
  ].join(',');

  const cardSelector = [
    '.product-card',
    '.collection-card',
    '.resource-card',
    '.resource-list__item',
  ].join(',');

  let revealObserver;

  const prefersReducedMotion = () => reducedMotionQuery.matches;

  const isElement = (node) => node instanceof HTMLElement;

  const getSectionContent = (section) => {
    if (!isElement(section)) return null;

    return [...section.children].find((child) => {
      return (
        isElement(child) &&
        !child.classList.contains('section-background') &&
        !child.matches('script, style, template')
      );
    });
  };

  const getTopLevelCards = (target) => {
    return [...target.querySelectorAll(cardSelector)].filter((card) => {
      return !card.parentElement?.closest(cardSelector);
    });
  };

  const setRevealProgress = (element, progress) => {
    element.style.setProperty('--ufm-item-progress', String(progress));
    element.style.setProperty('--ufm-item-reveal', progress === 1 ? '0%' : '100%');
    element.style.setProperty('--ufm-card-progress', String(progress));
    element.style.setProperty('--ufm-card-reveal', progress === 1 ? '0%' : '100%');
    element.style.setProperty('--ufm-media-progress', String(progress));
    element.style.setProperty('--ufm-media-reveal', progress === 1 ? '0%' : '100%');
  };

  const markCards = (target) => {
    getTopLevelCards(target).forEach((card, index) => {
      if (card.hasAttribute('data-ufm-reveal-card')) return;

      card.setAttribute('data-ufm-reveal-card', '');
      card.dataset.ufmCardIndex = String(index);
      card.style.setProperty('--ufm-reveal-delay', Math.min(index * 90, 420) + 'ms');
      setRevealProgress(card, 0);
    });
  };

  const markTextAndActions = (target) => {
    let itemIndex = 0;

    [...target.querySelectorAll(itemSelector)].forEach((element) => {
      if (
        element.closest('[data-ufm-reveal-card]') ||
        element.closest('[data-ufm-reveal-skip]') ||
        element.hasAttribute('data-ufm-reveal-item')
      ) {
        return;
      }

      const role = element.matches(
        'h1, h2, h3, h4, h5, h6, [class*="heading"], [class*="title"], .ufm-heading, .ufm-title'
        )
        ? 'heading'
        : element.matches(
              '.button, .button-secondary, .button-primary, .link, a.button, button'
            )
          ? 'cta'
          : element.matches(
                '.eyebrow, [class*="eyebrow"], [class*="kicker"], [class*="overline"]'
              )
            ? 'eyebrow'
            : 'body';

      element.setAttribute('data-ufm-reveal-item', role);
      element.dataset.ufmItemIndex = String(itemIndex);
      element.style.setProperty('--ufm-reveal-delay', Math.min(itemIndex * 85, 420) + 'ms');
      setRevealProgress(element, 0);
      itemIndex += 1;
    });
  };

  const markMedia = (target) => {
    const section = target.closest('.shopify-section') || target;

    const mediaSelector = [
        'img',
        'video',
        '.section-background',
        '[style*="background-image"]',
        '[class*="background"]',
        '[class*="media"]',
        '[class*="image"]',
    ].join(',');

    section.querySelectorAll(mediaSelector).forEach((media) => {
        if (media.hasAttribute('data-ufm-reveal-media')) return;
        if (media.closest('[data-ufm-reveal-skip]')) return;

        const backgroundImage = getComputedStyle(media).backgroundImage;

        const hasImageBackground =
        backgroundImage !== 'none' &&
        backgroundImage.includes('url(');

        if (!media.matches('img, video') && !hasImageBackground) {
        return;
        }

        media.setAttribute('data-ufm-reveal-media', '');

        media.dataset.ufmMediaDelay = media.closest(
        '[data-ufm-reveal-card]'
        )
        ? '0.08'
        : '0.02';
        media.style.setProperty('--ufm-reveal-delay', media.dataset.ufmMediaDelay === '0.08' ? '80ms' : '20ms');
        setRevealProgress(media, 0);
    });
    };

  const decorateSection = (target) => {
    markCards(target);
    markTextAndActions(target);
    markMedia(target);
  };

  const revealImmediately = (target) => {
    target.dataset.ufmRevealState = 'visible';
    revealObserver?.unobserve(target);
    target
      .querySelectorAll(
        '[data-ufm-reveal-item], [data-ufm-reveal-card], [data-ufm-reveal-media]'
      )
      .forEach((element) => setRevealProgress(element, 1));
  };

  const revealWhenVisible = (target) => {
    if (target.dataset.ufmRevealState === 'visible') return;

    target.dataset.ufmRevealState = 'pending';

    if (prefersReducedMotion() || !revealObserver) {
      revealImmediately(target);
      return;
    }

    revealObserver.observe(target);
  };

  const prepareSections = (main) => {
    const targets = [...main.querySelectorAll(sectionSelector)]
      .map(getSectionContent)
      .filter(Boolean);

    if (!targets.length) return;

    main.classList.add('ufm-reveal-ready', 'ufm-motion-ready');

    targets.forEach((target) => {
      if (!target.hasAttribute('data-ufm-reveal')) {
        target.setAttribute('data-ufm-reveal', 'section');
      }

      decorateSection(target);
      revealWhenVisible(target);
    });
  };

  const init = () => {
    const main = document.querySelector(mainSelector);

    if (!isElement(main)) return;

    const templateName = main.dataset.template || '';

    const isHomepage =
      templateName === '' ||
      templateName === 'index' ||
      templateName.startsWith('index.');

    if (!isHomepage) return;

    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              revealImmediately(entry.target);
            }
          });

        },
        {
          threshold: 0.08,
          rootMargin: '0px 0px -8% 0px',
        }
      );
    }

    prepareSections(main);
    const revealVisibleSections = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

      main
        .querySelectorAll("[data-ufm-reveal='section']")
        .forEach((target) => {
          const rect = target.getBoundingClientRect();

          if (rect.top < viewportHeight * 0.94 && rect.bottom > viewportHeight * 0.06) {
            revealImmediately(target);
          }
        });

    };

    window.addEventListener('scroll', revealVisibleSections, {
      passive: true,
    });

    window.addEventListener('resize', revealVisibleSections, {
      passive: true,
    });

    const sectionObserver = new MutationObserver(() => {
      window.requestAnimationFrame(() => {
        prepareSections(main);
      });
    });

    sectionObserver.observe(main, {
      childList: true,
      subtree: true,
    });

    const revealTimers = [
      window.setTimeout(revealVisibleSections, 40),
      // Never leave media permanently hidden when an editor preview restores the
      // page at an unexpected scroll position or IntersectionObserver is delayed.
      window.setTimeout(() => {
        main
          .querySelectorAll("[data-ufm-reveal='section']")
          .forEach(revealImmediately);
      }, 2200),
    ];

    cleanup = () => {
      revealObserver?.disconnect();
      sectionObserver.disconnect();
      revealTimers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener('scroll', revealVisibleSections);
      window.removeEventListener('resize', revealVisibleSections);
      cleanup = undefined;
    };

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:unload', () => {
    if (!document.querySelector(mainSelector)) cleanup?.();
  });
})();
